import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { MessageSquare, FileSearch, Paintbrush, Send } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Inquiry',
    description: 'Share your project details through the brief form. Tell me about your brand, goals, and vision.',
    icon: MessageSquare,
  },
  {
    number: '02',
    title: 'Review',
    description: 'I review your brief, research your market, and prepare a tailored proposal with timeline and pricing.',
    icon: FileSearch,
  },
  {
    number: '03',
    title: 'Creation',
    description: 'Design work begins. You receive concepts, provide feedback, and we refine until it is perfect.',
    icon: Paintbrush,
  },
  {
    number: '04',
    title: 'Delivery',
    description: 'Final files delivered in all formats needed. Your project is ready to launch and make an impact.',
    icon: Send,
  },
];

const ProcessStep = ({ step, index, isLast }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const Icon = step.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      data-testid={`process-step-${index + 1}`}
      className="relative"
    >
      <div className="flex flex-col items-center text-center">
        {/* Step Number */}
        <div className="step-number mb-6">
          {step.number}
        </div>

        {/* Icon */}
        <div className="w-12 h-12 rounded-full bg-card border border-border/50 flex items-center justify-center mb-4">
          <Icon className="w-5 h-5 text-primary" />
        </div>

        {/* Title */}
        <h3 className="font-['Sora'] text-lg font-semibold text-foreground mb-2">
          {step.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed max-w-[240px]">
          {step.description}
        </p>
      </div>

      {/* Connector Line (hidden on mobile and last item) */}
      {!isLast && (
        <div className="hidden lg:block absolute top-12 left-[calc(50%+60px)] w-[calc(100%-120px)] h-px bg-border/50">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary/50" />
        </div>
      )}
    </motion.div>
  );
};

export const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-24 md:py-32 px-6 lg:px-8 bg-card/30"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Process
          </span>
          <h2
            data-testid="process-title"
            className="font-['Sora'] text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-2 tracking-tight"
          >
            How It Works
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            A streamlined process designed to bring your vision to life efficiently and effectively.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {steps.map((step, index) => (
            <ProcessStep
              key={step.number}
              step={step}
              index={index}
              isLast={index === steps.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
